/**
 * Display helpers for POST /get_usage feature objects.
 * Never derive percentage from used/limit (breaks when limit === -1).
 */

/** API feature keys on GET /get_usage → legacy USAGE_FEATURES keys */
export const USAGE_API_FEATURE_KEYS = {
  biq: 'biq',
  deep_thinking: 'deep_thinking',
  analytics: 'analytics',
  smart_alerts: 'smart_alerts',
  custom_alerts: 'custom_alerts',
  pdf_reports: 'pdf_reports',
};

/**
 * @param {object|null|undefined} raw
 * @param {number} [fallbackLimit]
 * @returns {{ used: number, limit: number, percentage: number, is_unlimited: boolean }}
 */
export function normalizeFeatureUsage(raw, fallbackLimit = 0) {
  if (!raw || typeof raw !== 'object') {
    const unlimited = fallbackLimit === -1 || fallbackLimit === Infinity;
    return {
      used: 0,
      limit: unlimited ? -1 : fallbackLimit,
      percentage: 0,
      is_unlimited: unlimited,
    };
  }

  const used = Math.max(0, Number(raw.used ?? raw.count ?? 0) || 0);
  let limit = raw.limit;
  if (limit === undefined || limit === null) {
    if (fallbackLimit === Infinity) limit = -1;
    else limit = fallbackLimit;
  } else {
    limit = Number(limit);
  }

  const is_unlimited =
    raw.is_unlimited === true || limit === -1 || fallbackLimit === Infinity;

  const percentage = is_unlimited
    ? 0
    : Math.max(0, Math.min(100, Number(raw.percentage ?? 0) || 0));

  return { used, limit, percentage, is_unlimited };
}

/**
 * @param {{ used: number, limit: number, percentage: number, is_unlimited: boolean }} feature
 */
export function getUsageDisplay(feature) {
  if (feature.is_unlimited || feature.limit === -1) {
    return {
      label: 'Unlimited',
      percentage: 0,
      barFillPercent: 0,
      detail: `${feature.used.toLocaleString()} used`,
    };
  }

  if (feature.limit === 0) {
    const pct = feature.used > 0 ? 100 : 0;
    return {
      label: feature.used > 0 ? 'Not available' : '0%',
      percentage: pct,
      barFillPercent: pct,
      detail: `${feature.used} / ${feature.limit}`,
    };
  }

  return {
    label: `${feature.percentage}%`,
    percentage: feature.percentage,
    barFillPercent: feature.percentage,
    detail: `${feature.used.toLocaleString()} / ${feature.limit.toLocaleString()}`,
  };
}
